import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { filter, map, startWith, take } from 'rxjs/operators';

import { Authority, HighSchool, Organization, Prefecture } from 'src/app/models/common-data';
import { enter } from 'src/app/resources/animations';
import { Log } from 'src/app/utils/log';
import { RootState } from 'src/app/reducers';
import { createUser, initializeCreateUserState } from 'src/app/actions/user.actions';
import { User } from 'src/app/models/user';
import { getCreateUserResult } from 'src/app/selectors/user.selectors';
import { dispatchInfoMessage } from 'src/app/actions/core.actions';
import {
  ACCOUNT_DIALOG_TYPE_ACCOUNTS,
  DAIGAKU_ORGANIZATION_ID,
  JUKU_ORGANIZATION_ID,
  OBUNSHA_NAME,
  OBUNSHA_ORGANIZATION_ID,
  ORGANIZATIONS,
  PREFECTURES,
  SCHOOL_ORGANIZATION_ID,
  TRY_ORGANIZATION_ID,
  TRY_ORGANIZATION_NAME,
  USER_AUTHORITIES,
  USER_AUTHORITY_OBUNSHA_ID,
  USER_AUTHORITY_ORGANIZATION_MASTER_ID,
  USER_AUTHORITY_ORGANIZATION_USER_ID
} from '../../../resources/config';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { Observable } from 'rxjs';
import { CustomValidators } from '../../../utils/custom-validator';
import { Juku } from '../../../models/juku';
import { Daigaku } from 'src/app/models/daigaku';
import { DaigakuUtil } from 'src/app/utils/daigaku-util';

export interface AddAccountDialogData {
  signInUser: User;
  highSchools: HighSchool[];
  jukus: Juku[];
  daigakus: Daigaku[];
  type?: string;
  defaultOrganization?: string;
  defaultSchool?: string;
  defaultSchoolId?: string;
}

@Component({
  selector: 'app-add-account-dialog',
  templateUrl: './add-account-dialog.component.html',
  styleUrls: ['./add-account-dialog.component.scss'],
  animations: [enter]
})
export class AddAccountDialogComponent implements OnInit, OnDestroy {
  userAuthorityObunshaId: number;
  userAuthorityOrganizationMasterId: number;
  userAuthorityOrganizationUserId: number;
  signInUser: User;
  accountForm: UntypedFormGroup;
  organizations: Organization[];
  highSchools: HighSchool[];
  jukus: Juku[];
  daigakus: Daigaku[];
  filteredSchools$: Observable<HighSchool[]>;
  prefectures: Prefecture[];
  authorities: Authority[];
  schoolId: string;
  defaultOrganization: string;
  defaultSchool: string;
  defaultSchoolId: string;
  schoolVisibleFlag: boolean;
  dialogTypeAccounts: string;
  dialogType: string;
  dialogTitle: string;
  accountCreating = false;
  errorMessage = '';
  /** two-way binding */
  adminDisabled = true;
  organizationMasterDisabled = false;
  private LOG_SOURCE = this.constructor.name;

  constructor(
    private store: Store<RootState>,
    private dialogRef: MatDialogRef<AddAccountDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: AddAccountDialogData
  ) {}

  ngOnInit() {
    this.userAuthorityObunshaId = USER_AUTHORITY_OBUNSHA_ID;
    this.userAuthorityOrganizationMasterId = USER_AUTHORITY_ORGANIZATION_MASTER_ID;
    this.userAuthorityOrganizationUserId = USER_AUTHORITY_ORGANIZATION_USER_ID;
    this.signInUser = this.data.signInUser;
    this.organizations = ORGANIZATIONS;
    this.highSchools = [...this.data.highSchools];
    this.jukus = [...this.data.jukus];
    this.daigakus = [...this.data.daigakus];
    this.prefectures = PREFECTURES;
    this.authorities = USER_AUTHORITIES;
    this.defaultOrganization = this.data.defaultOrganization;
    this.defaultSchool = this.data.defaultSchool;
    this.defaultSchoolId = this.data.defaultSchoolId;
    this.schoolVisibleFlag = !(this.defaultOrganization && this.defaultSchool && this.defaultSchoolId);
    this.dialogTypeAccounts = ACCOUNT_DIALOG_TYPE_ACCOUNTS;
    this.dialogType = this.data.type;
    this.dialogTitle = this.dialogType === ACCOUNT_DIALOG_TYPE_ACCOUNTS ? 'ユーザーアカウントを登録' : 'メンバーの登録';

    this.setUpForms();
    this.setUpSchool();
  }

  ngOnDestroy() {
    this.store.dispatch(initializeCreateUserState());
  }

  createAccount() {
    if (this.accountForm.invalid) {
      this.accountForm.markAllAsTouched();
      return;
    }
    Log.debug(this.LOG_SOURCE, 'アカウントを作成します');
    this.disableForms();
    this.errorMessage = '';

    const familyName: string = this.accountForm.get('familyName').value;
    const firstName: string = this.accountForm.get('firstName').value;
    const familyNameKana: string = this.accountForm.get('familyNameKana').value;
    const firstNameKana: string = this.accountForm.get('firstNameKana').value;
    const email: string = this.accountForm.get('email').value;
    const organizationId: string = this.schoolVisibleFlag ? this.accountForm.get('organization').value.id : this.defaultOrganization;
    const school: string = this.schoolVisibleFlag ? this.accountForm.get('school').value : this.defaultSchool;
    const authority: number = this.accountForm.get('authority').value;

    if (!this.schoolVisibleFlag) {
      this.schoolId = this.defaultSchoolId;
    } else if (this.accountForm.get('organization').value.id === SCHOOL_ORGANIZATION_ID) {
      if (this.highSchools.find(highSchool => highSchool.name === school) === undefined) {
        this.schoolId = SCHOOL_ORGANIZATION_ID;
      }
    } else if (this.accountForm.get('organization').value.id === JUKU_ORGANIZATION_ID) {
      if (this.jukus.find(juku => this.getJukuNameForFiltered(juku) === school) === undefined) {
        this.schoolId = JUKU_ORGANIZATION_ID;
      }
    } else if (this.accountForm.get('organization').value.id === DAIGAKU_ORGANIZATION_ID) {
      if (this.daigakus.find(daigaku => DaigakuUtil.getDisplayDaigakuName(daigaku) === school) === undefined) {
        this.schoolId = DAIGAKU_ORGANIZATION_ID;
      }
    }

    const user: User = {
      familyName,
      firstName,
      email,
      organization: organizationId,
      schoolId: this.schoolId,
      school,
      isAdmin: authority === USER_AUTHORITY_OBUNSHA_ID,
      isOrganizationAdmin: authority === USER_AUTHORITY_ORGANIZATION_MASTER_ID
    };

    if (familyNameKana !== '') user.familyNameKana = familyNameKana;
    if (firstNameKana !== '') user.firstNameKana = firstNameKana;

    this.store.dispatch(createUser({ user }));

    this.store
      .select(getCreateUserResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        this.store.dispatch(initializeCreateUserState());
        Log.debug(this.LOG_SOURCE, `create user result: `, result);
        if (result.success) {
          const message = this.schoolVisibleFlag
            ? `${email} をアカウント登録しました`
            : 'メンバー登録しました。続いてご契約プランへ割当を行う必要があります。';
          this.dialogRef.close(true);
          this.store.dispatch(
            dispatchInfoMessage({
              message
            })
          );
          return;
        }

        Log.warn(this.LOG_SOURCE, `create user error: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
        this.enableForms();
        // TODO: メッセージ内容を要検討
        this.errorMessage = result.error ? `[${result.error.code}] ${result.error.message}` : 'エラーが発生しました';
      });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  // components internal methods ---------------------------------------------------------------
  onChangeOrganization() {
    this.schoolId = null;
    this.accountForm.patchValue({ school: null });

    this.changeConditionIsAdmin();
    this.setOrganizationName();
    this.setUpSchool();
    this.setOrganizationMasterDisabled();

    if (
      this.accountForm.get('organization').value.id !== SCHOOL_ORGANIZATION_ID &&
      this.accountForm.get('organization').value.id !== JUKU_ORGANIZATION_ID &&
      this.accountForm.get('organization').value.id !== DAIGAKU_ORGANIZATION_ID
    ) {
      this.schoolId = this.accountForm.get('organization').value.id;
    }
  }

  onChangePrefecture() {
    this.changeConditionIsAdmin();
    this.setUpSchool();
  }

  onSelectSchool(event: MatAutocompleteSelectedEvent) {
    this.schoolId = event.option.value.id.trim();
    this.accountForm.patchValue({ school: event.option.value.name });
  }

  // set ups or private method ---------------------------------------------------------------
  private setUpForms() {
    this.accountForm = new UntypedFormGroup({
      familyName: new UntypedFormControl('', [Validators.required]),
      firstName: new UntypedFormControl('', [Validators.required]),
      familyNameKana: new UntypedFormControl('', []),
      firstNameKana: new UntypedFormControl('', []),
      email: new UntypedFormControl('', [Validators.required, Validators.email]),
      organization: new UntypedFormControl('', this.schoolVisibleFlag ? [Validators.required] : []),
      prefecture: new UntypedFormControl('', []),
      school: new UntypedFormControl(
        '',
        this.schoolVisibleFlag
          ? [
              Validators.required,
              CustomValidators.isSchoolInMaster(this.highSchools.map(school => school.name)),
              CustomValidators.isJukuInMaster(this.jukus.map(juku => this.getJukuNameForFiltered(juku))),
              CustomValidators.isDaigakuInMaster(this.daigakus.map(daigaku => DaigakuUtil.getDisplayDaigakuName(daigaku)))
            ]
          : []
      ),
      authority: new UntypedFormControl('', [Validators.required])
    });

    if (this.schoolVisibleFlag === true || this.signInUser.isAdmin) {
      this.accountForm.get('authority').setValue(USER_AUTHORITY_ORGANIZATION_MASTER_ID);
    } else {
      this.accountForm.get('authority').setValue(USER_AUTHORITY_ORGANIZATION_USER_ID);
    }
  }

  private setUpSchool() {
    this.filteredSchools$ = this.accountForm.get('school').valueChanges.pipe(
      startWith(null as string),
      map(value => this.changefilteredSchools(value))
    );
  }

  private changefilteredSchools(value: string): HighSchool[] {
    let filteredSchools = [];

    if (this.accountForm.get('organization').value.id === SCHOOL_ORGANIZATION_ID) {
      filteredSchools = this.highSchools;
    } else if (this.accountForm.get('organization').value.id === JUKU_ORGANIZATION_ID) {
      filteredSchools = this.jukus.map(juku => {
        return {
          id: juku.jukuCode,
          name: this.getJukuNameForFiltered(juku),
          prefectureId: juku.prefectureId
        };
      });
    } else if (this.accountForm.get('organization').value.id === DAIGAKU_ORGANIZATION_ID) {
      filteredSchools = this.daigakus.map(daigaku => {
        return {
          id: daigaku.daigakuCode,
          name: DaigakuUtil.getDisplayDaigakuName(daigaku),
          prefectureId: daigaku.prefectureId
        };
      });
    }

    if (this.accountForm.get('prefecture').value) {
      filteredSchools = filteredSchools.filter(highSchool => highSchool.prefectureId === this.accountForm.get('prefecture').value);
    }

    value = value ? value : this.accountForm.get('school').value;
    return value ? filteredSchools.filter(highSchool => highSchool.name.indexOf(value) !== -1) : filteredSchools;
  }

  private changeConditionIsAdmin() {
    this.adminDisabled = this.accountForm.get('organization').value.id !== OBUNSHA_ORGANIZATION_ID;

    if (this.adminDisabled) {
      if (this.accountForm.get('authority').value === USER_AUTHORITY_OBUNSHA_ID) {
        this.accountForm.get('authority').setValue(USER_AUTHORITY_ORGANIZATION_MASTER_ID);
      }
    }
  }

  private setOrganizationMasterDisabled() {
    this.organizationMasterDisabled = this.accountForm.get('organization').value.id === TRY_ORGANIZATION_ID;

    if (this.organizationMasterDisabled) {
      this.accountForm.get('authority').setValue(USER_AUTHORITY_ORGANIZATION_USER_ID);
    }
  }

  private setOrganizationName() {
    if (this.accountForm.get('organization').value.id === OBUNSHA_ORGANIZATION_ID) {
      this.accountForm.patchValue({
        school: OBUNSHA_NAME
      });
    }
    if (this.accountForm.get('organization').value.id === TRY_ORGANIZATION_ID) {
      this.accountForm.patchValue({
        school: TRY_ORGANIZATION_NAME
      });
    }
  }

  private disableForms() {
    Object.keys(this.accountForm.controls).forEach(ctrlName => {
      this.accountForm.get(ctrlName).disable();
    });
    this.adminDisabled = true;
    this.accountCreating = true;
  }

  private enableForms() {
    Object.keys(this.accountForm.controls).forEach(ctrlName => {
      this.accountForm.get(ctrlName).enable();
    });
    this.adminDisabled = this.accountForm.get('organization').value.id !== OBUNSHA_ORGANIZATION_ID;
    this.accountCreating = false;
  }

  private getJukuNameForFiltered(juku: Juku): string {
    return juku.jukuName + (juku.jukuSubName ? '（' + juku.jukuSubName + '）' : '');
  }
}
